/**
 * Meta-admin organizations area — list / create / suspend / reactivate every
 * client organization, and "enter" one (sets the active org scope, then routes
 * into that org's ticket feed so the meta-admin can operate within it).
 */
import { useState, type FormEvent, type ReactElement } from 'react';
import { useNavigate } from 'react-router-dom';
import type { OrgPlan, Organization } from '@hotline/contracts';

import { useAuth } from '../../auth/AuthContext.js';
import { useOrgScope } from '../../org/OrgScopeContext.js';
import {
  Alert,
  Badge,
  Button,
  EmptyState,
  Input,
  PageHeader,
  Section,
  Select,
  Spinner,
  StatusPill,
  Toolbar,
} from '../../components/ui/index.js';
import { Icon } from '../../components/ui/icons.js';
import { useAsync, toMessage } from '../../lib/useAsync.js';

const PLANS: OrgPlan[] = ['free', 'pro', 'enterprise'];

/**
 * The design org-logo is a colored rounded square with bold initials. Real org
 * data has no brand color, so derive a stable tint from the name (literal hues
 * from the design's swatch family) and take up to two leading initials.
 */
const LOGO_TINTS = [
  '#F5392B',
  '#FF5A1F',
  '#E11D48',
  '#7C5CFC',
  '#0EA5E9',
  '#10B981',
];

function logoTint(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i += 1) {
    hash = (hash * 31 + name.charCodeAt(i)) | 0;
  }
  return LOGO_TINTS[Math.abs(hash) % LOGO_TINTS.length] ?? '#F5392B';
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const [first, second] = parts;
  if (!first) return '·';
  if (!second) return first.slice(0, 2).toUpperCase();
  return ((first[0] ?? '') + (second[0] ?? '')).toUpperCase();
}

export default function OrgsPage(): ReactElement {
  const { client } = useAuth();
  const { setOrgId } = useOrgScope();
  const navigate = useNavigate();

  const orgs = useAsync<Organization[]>(() => client.api.listOrganizations(), [client]);
  const [name, setName] = useState('');
  const [plan, setPlan] = useState<OrgPlan>('free');
  const [createError, setCreateError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const createOrg = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setCreateError(null);
    try {
      await client.api.createOrganization({ name, plan });
      setName('');
      setPlan('free');
      orgs.reload();
    } catch (err) {
      setCreateError(toMessage(err));
    }
  };

  const toggleSuspend = async (org: Organization): Promise<void> => {
    setBusyId(org.id);
    try {
      const updated =
        org.status === 'suspended'
          ? await client.api.reactivateOrganization(org.id)
          : await client.api.suspendOrganization(org.id);
      orgs.setData((prev) =>
        (prev ?? []).map((o) => (o.id === updated.id ? updated : o)),
      );
    } catch (err) {
      setCreateError(toMessage(err));
    } finally {
      setBusyId(null);
    }
  };

  const manageOrg = (org: Organization): void => {
    // Scope follows the click, so the org's own screens work if the operator
    // navigates on from the editor.
    setOrgId(org.id);
    navigate(`/meta/orgs/${org.id}`);
  };

  return (
    <>
      <PageHeader
        title="Organizations"
        description="Meta-admin · create, suspend, and enter every client organization."
      />

      <Section
        title="Create organization"
        description="Provision a new client org and choose its plan."
      >
        <form onSubmit={createOrg} aria-label="Create organization">
          <Toolbar className="rounded-lg border border-border bg-surface p-3 shadow-card">
            <Input
              aria-label="Organization name"
              placeholder="New organization name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              className="sm:w-72"
            />
            <Select
              aria-label="Plan"
              value={plan}
              onChange={(e) => setPlan(e.target.value as OrgPlan)}
              className="capitalize sm:w-40"
            >
              {PLANS.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </Select>
            <Toolbar.Spacer />
            <Button type="submit" variant="primary">
              <Icon.plus size={15} />
              Create
            </Button>
          </Toolbar>
        </form>
      </Section>

      {createError ? <Alert className="mb-6">{createError}</Alert> : null}

      {orgs.loading ? <Spinner label="Loading organizations…" /> : null}

      {orgs.error ? <Alert>{orgs.error}</Alert> : null}

      {orgs.data && orgs.data.length === 0 ? (
        <EmptyState
          title="No organizations yet"
          description="Create one above."
        />
      ) : null}

      {orgs.data && orgs.data.length > 0 ? (
        <div className="overflow-hidden rounded-lg border border-border bg-surface shadow-card">
          {/* table head — design `.tbl-head` (uppercase, faint, sticky-friendly) */}
          <div
            className="grid grid-cols-[1fr_120px_140px_130px_auto] items-center gap-4 border-b border-border bg-surface-2/60 px-6 py-[9px] text-[10.5px] font-extrabold uppercase tracking-[0.08em] text-faint"
            role="row"
          >
            <div role="columnheader">Organization</div>
            <div role="columnheader">Plan</div>
            <div role="columnheader">Status</div>
            <div role="columnheader">Created</div>
            <div role="columnheader" aria-label="Actions" />
          </div>

          {/* org rows — design `.org-row` */}
          <ul className="m-0 list-none p-0">
            {orgs.data.map((org) => (
              <li
                key={org.id}
                data-org-id={org.id}
                data-status={org.status}
                role="row"
                className="grid grid-cols-[1fr_120px_140px_130px_auto] items-center gap-4 border-b border-border px-6 py-[11px] transition-colors last:border-b-0 hover:bg-surface-hover"
              >
                {/* logo + name */}
                <div className="flex min-w-0 items-center gap-[11px]" role="cell">
                  <span
                    aria-hidden="true"
                    className="grid size-[30px] flex-shrink-0 place-items-center rounded-lg text-[12px] font-extrabold text-white"
                    style={{ background: logoTint(org.name) }}
                  >
                    {initials(org.name)}
                  </span>
                  <div className="min-w-0">
                    <div className="truncate text-[13.5px] font-bold text-text">
                      {org.name}
                    </div>
                    <div className="mono truncate text-[11.5px] text-faint">
                      {org.id}
                    </div>
                  </div>
                </div>

                {/* plan */}
                <div role="cell">
                  <Badge square className="capitalize">
                    {org.plan}
                  </Badge>
                </div>

                {/* status */}
                <div role="cell">
                  <StatusPill status={org.status} />
                </div>

                {/* created */}
                <div className="text-[12.5px] text-muted" role="cell">
                  {new Date(org.createdAt).toLocaleDateString()}
                </div>

                {/* actions */}
                <div
                  className="flex items-center justify-end gap-2"
                  role="cell"
                >
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => manageOrg(org)}
                  >
                    <Icon.eye size={14} />
                    Manage
                  </Button>
                  <Button
                    size="sm"
                    variant="danger-ghost"
                    disabled={busyId === org.id}
                    onClick={() => toggleSuspend(org)}
                  >
                    <Icon.pause2 size={14} />
                    {org.status === 'suspended' ? 'Reactivate' : 'Suspend'}
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </>
  );
}
